import { useState } from "react";
import { Button, Chip, Divider, Paper, Stack, Typography } from "@mui/material";
import PaidIcon from "@mui/icons-material/Paid";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  DateField,
  FunctionField,
  Show,
  SimpleShowLayout,
  TextField,
  useNotify,
  usePermissions,
  useRecordContext,
  useRefresh,
} from "react-admin";
import { Link } from "react-router-dom";

import { adminApi } from "../../dataProvider";
import { orderStatus } from "./status";
import { PASSPORT_FIELDS, kycAnswerLabel, passportValue } from "../kyc/labels";

type Requisites = {
  beneficiary_bank: string;
  corr_account: string;
  bik: string;
  recipient_name: string;
  recipient_inn: string;
  recipient_kpp: string;
  settlement_account: string;
  purpose: string;
};

type CryptoTx = { tx_hash: string | null; status: string; error: string | null };

type OrderRecord = {
  id: string;
  reference: string;
  broker_client_id: number | null;
  status: string;
  cancel_reason: string | null;
  requisites: Requisites | null;
  kyc_applicant_id: string | null;
  kyc_snapshot: Record<string, unknown> | null;
  crypto_tx?: CryptoTx | null;
};

function StatusChip() {
  const record = useRecordContext<OrderRecord>();
  if (!record) return null;
  const s = orderStatus(record.status);
  return <Chip size="small" color={s.color} label={s.label} />;
}

function ActionButtons() {
  const record = useRecordContext<OrderRecord>();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  if (!record) return null;
  const st = record.status;

  const call = async (action: "paid" | "reject", json?: unknown, okMsg?: string) => {
    setBusy(true);
    try {
      await adminApi(`/admin/orders/${record.id}/${action}`, { method: "POST", json });
      notify(okMsg ?? "Готово", { type: "success" });
      refresh();
    } catch (e) {
      notify(e instanceof Error ? e.message : "Ошибка", { type: "error" });
    } finally {
      setBusy(false);
    }
  };

  const reject = () => {
    const reason = window.prompt("Причина отклонения (увидит клиент):");
    if (reason === null) return;
    call("reject", { reason }, "Заявка отклонена");
  };

  if (st === "completed") return <Chip color="success" icon={<PaidIcon />} label="Выполнена" />;
  if (st === "sending") return <Chip color="info" label="Отправка…" />;
  if (st === "rejected") return <Chip color="error" icon={<CancelIcon />} label="Отклонена" />;
  if (st === "cancelled") return <Chip icon={<CancelIcon />} label="Отменена клиентом" />;

  const canReject = st === "awaiting_kyc" || st === "awaiting_payment" || st === "paid";

  return (
    <Stack spacing={1} alignItems="flex-start">
      <Stack direction="row" spacing={2}>
        {(st === "awaiting_payment" || st === "awaiting_kyc") && (
          <Button
            variant="contained"
            color="success"
            startIcon={<PaidIcon />}
            disabled={busy || st === "awaiting_kyc"}
            onClick={() => call("paid", undefined, "Заявка отмечена оплаченной")}
          >
            Оплачено
          </Button>
        )}
        {canReject && (
          <Button variant="outlined" color="error" startIcon={<CancelIcon />} disabled={busy} onClick={reject}>
            Отклонить
          </Button>
        )}
      </Stack>
      {st === "awaiting_kyc" && (
        <Typography variant="caption" color="text.secondary">
          KYC ещё не подтверждён — реквизиты не выданы.
        </Typography>
      )}
      {st === "send_failed" && record.crypto_tx?.error && (
        <Typography variant="caption" color="error">
          {record.crypto_tx.error}
        </Typography>
      )}
    </Stack>
  );
}

const REQ_ROWS: { key: keyof Requisites; label: string }[] = [
  { key: "beneficiary_bank", label: "Банк получателя" },
  { key: "corr_account", label: "Корр. счёт" },
  { key: "bik", label: "БИК" },
  { key: "recipient_name", label: "Получатель" },
  { key: "recipient_inn", label: "ИНН" },
  { key: "recipient_kpp", label: "КПП" },
  { key: "settlement_account", label: "Расчётный счёт" },
  { key: "purpose", label: "Назначение" },
];

function RequisitesBlock() {
  const record = useRecordContext<OrderRecord>();
  const req = record?.requisites;
  if (!req) {
    return (
      <Typography variant="body2" color="text.secondary">
        Реквизиты не выданы (ожидается подтверждение KYC).
      </Typography>
    );
  }
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1}>
        {REQ_ROWS.map((row) => (
          <Stack key={row.key} direction="row" spacing={2}>
            <Typography variant="caption" sx={{ width: 160, color: "text.secondary" }}>
              {row.label}
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>
              {req[row.key]}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

function KycBlock() {
  const record = useRecordContext<OrderRecord>();
  const snap = record?.kyc_snapshot as
    | { parsed_passport?: Record<string, unknown> | null; review_answer?: string | null }
    | null
    | undefined;
  const passport = snap?.parsed_passport;
  return (
    <Stack spacing={1.5}>
      {record?.kyc_applicant_id ? (
        <Button
          component={Link}
          to={`/kyc-applicants/${record.kyc_applicant_id}/show`}
          size="small"
          variant="outlined"
          sx={{ alignSelf: "flex-start" }}
        >
          Открыть KYC-анкету
        </Button>
      ) : (
        <Typography variant="body2" color="text.secondary">
          KYC-анкета ещё не привязана.
        </Typography>
      )}
      {snap?.review_answer && (
        <Chip
          size="small"
          color={snap.review_answer === "GREEN" ? "success" : "error"}
          label={kycAnswerLabel(snap.review_answer)}
          sx={{ alignSelf: "flex-start" }}
        />
      )}
      {passport && !passport._error && (
        <Stack spacing={1}>
          {PASSPORT_FIELDS.filter(({ key }) => passport[key] != null && passport[key] !== "").map(({ key, label }) => (
            <Stack key={key} direction="row" spacing={2}>
              <Typography variant="body2" sx={{ width: 160, flexShrink: 0, color: "text.secondary" }}>
                {label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {passportValue(key, passport[key])}
              </Typography>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function CryptoTxBlock() {
  const record = useRecordContext<OrderRecord>();
  const tx = record?.crypto_tx;
  if (!tx) {
    return (
      <Typography variant="body2" color="text.secondary">
        USDT ещё не отправлялся.
      </Typography>
    );
  }
  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">Статус: {tx.status}</Typography>
      {tx.tx_hash && (
        <a href={`https://tronscan.org/#/transaction/${tx.tx_hash}`} target="_blank" rel="noreferrer">
          {tx.tx_hash}
        </a>
      )}
      {tx.error && (
        <Typography variant="caption" color="error">
          {tx.error}
        </Typography>
      )}
    </Stack>
  );
}

export function OrderShow() {
  const { permissions } = usePermissions();
  const isAdmin = permissions === "admin";
  return (
    <Show>
      <SimpleShowLayout>
        <TextField source="reference" label="Заявка" />
        <FunctionField
          label="ЛС/ДБО (cid)"
          render={(r: OrderRecord) => (r.broker_client_id != null ? String(r.broker_client_id) : "— не онбордён")}
        />
        <FunctionField label="Статус" render={() => <StatusChip />} />
        <FunctionField label="Действие" render={() => <ActionButtons />} />
        <FunctionField
          label="Причина отклонения"
          render={(r: { status: string; cancel_reason: string | null }) =>
            r.status === "rejected" ? (
              <Typography variant="body2" color="error">
                {r.cancel_reason || "—"}
              </Typography>
            ) : null
          }
        />
        <Divider />
        <FunctionField label="Сумма ₽" render={(r: { amount_rub: number }) => r.amount_rub.toLocaleString("ru-RU")} />
        <FunctionField label="USDT" render={(r: { amount_usdt: number }) => r.amount_usdt.toFixed(6)} />
        <FunctionField label="Курс" render={(r: { rate: number }) => r.rate.toFixed(2)} />
        <TextField source="wallet_address" label="Кошелёк" sx={{ fontFamily: "monospace" }} />
        {isAdmin && <TextField source="network" label="Сеть" />}
        <TextField source="user_phone" label="Телефон" />
        {isAdmin && <TextField source="user_username" label="Telegram" />}
        <DateField source="created_at" label="Создана" showTime />
        <DateField source="requisites_issued_at" label="Реквизиты выданы" showTime />
        <DateField source="paid_at" label="Оплачена" showTime />
        {isAdmin && <FunctionField label="Транзакция USDT" render={() => <CryptoTxBlock />} />}
        <FunctionField label="Реквизиты для перевода" render={() => <RequisitesBlock />} />
        <FunctionField label="KYC клиента" render={() => <KycBlock />} />
      </SimpleShowLayout>
    </Show>
  );
}
